import {
  ActionReducerMapBuilder,
  AsyncThunk,
  PayloadAction,
  createSlice,
} from "@reduxjs/toolkit";
import {
  WalletPresets,
  WalletSummary,
  WalletTransaction,
} from "../../../utils/wallet/walletTypes";
import {
  WalletFetchArg,
  fetchLatestWalletTransactions,
  fetchWalletSummary,
} from "../http/walletThunks";

type WalletResource<T> = {
  data: T | null;
  restaurantId: number | null;
  requestId: string | null;
  status: "idle" | "loading" | "succeeded" | "failed";
};

type WalletState = {
  summary: WalletResource<WalletSummary>;
  /**
   * A plain value, not a resource: the presets only ever arrive baked into
   * the page (setBakedWalletPresets), so there is no request to be loading,
   * failed or superseded, and no restaurant to scope them to -- a partner
   * group shares one account and its presets.
   */
  presets: WalletPresets | null;
  latestTransactions: WalletResource<WalletTransaction[]>;
};

/** The parts of the state that are fetched, and so carry request metadata. */
type WalletResourceKey = "summary" | "latestTransactions";

const emptyResource = <T>(): WalletResource<T> => ({
  data: null,
  restaurantId: null,
  requestId: null,
  status: "idle",
});

const initialState: WalletState = {
  summary: emptyResource(),
  presets: null,
  latestTransactions: emptyResource(),
};

/**
 * Same guards as loyaltyTierSystemSlice: data from another restaurant is
 * dropped as soon as a fetch for this one starts, and only the latest request
 * may write. A failure keeps the last good data: "the wallet service is down"
 * must not blank a balance the customer just saw. "This customer has no
 * wallet yet" is not a failure -- fetchWalletSummary resolves it as `null`.
 */
const addResourceCases = <T>(
  builder: ActionReducerMapBuilder<WalletState>,
  key: WalletResourceKey,
  thunk: AsyncThunk<T, WalletFetchArg, object>
) => {
  const resource = (state: WalletState) =>
    state[key] as WalletResource<unknown>;
  const isCurrent = (
    state: WalletState,
    meta: { arg: WalletFetchArg; requestId: string }
  ) =>
    resource(state).restaurantId === meta.arg.restaurantId &&
    resource(state).requestId === meta.requestId;

  builder
    .addCase(thunk.pending, (state, { meta }) => {
      const current = resource(state);
      if (current.restaurantId !== meta.arg.restaurantId) {
        current.data = null;
      }
      current.restaurantId = meta.arg.restaurantId;
      current.requestId = meta.requestId;
      current.status = "loading";
    })
    .addCase(thunk.fulfilled, (state, { meta, payload }) => {
      if (!isCurrent(state, meta)) {
        return;
      }
      resource(state).status = "succeeded";
      resource(state).data = payload;
    })
    .addCase(thunk.rejected, (state, { meta }) => {
      if (!isCurrent(state, meta)) {
        return;
      }
      resource(state).status = "failed";
    });
};

const walletSlice = createSlice({
  name: "wallet",
  initialState,
  reducers: {
    /** On sign-out: the balance and history belong to the old customer. The
     * presets are the restaurant's and stay. */
    clearWalletCustomerData: (state) => {
      state.summary = emptyResource();
      state.latestTransactions = emptyResource();
    },
    clearWallet: () => initialState,
    /** Presets baked into the page at build time (getWalletPresetsStaticProps),
     * stored as if they had just been fetched for that restaurant. */
    setBakedWalletPresets: (state, action: PayloadAction<WalletPresets>) => {
      state.presets = action.payload;
    },
  },
  extraReducers: (builder) => {
    addResourceCases(builder, "summary", fetchWalletSummary);
    addResourceCases(
      builder,
      "latestTransactions",
      fetchLatestWalletTransactions
    );
  },
});

export const { clearWalletCustomerData, clearWallet, setBakedWalletPresets } =
  walletSlice.actions;

export default walletSlice.reducer;
